import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import { roleService, Role } from '../services/roleService';
import { permissionService, Permission } from '../services/permissionService';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/common/Icon';
import { 
  HiOutlineUserGroup, 
  HiOutlinePlus, 
  HiOutlinePencil, 
  HiOutlineTrash,
  HiOutlineShieldCheck,
  HiOutlineX
} from 'react-icons/hi';
import { FiEdit2, FiTrash2, FiSave, FiX } from 'react-icons/fi';

const Roles: React.FC = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only org_admin can access roles
    if (user?.role !== 'org_admin') {
      setError('You do not have permission to access roles');
      return;
    }

    // Prevent duplicate calls from React StrictMode
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    loadData();
  }, [user]);

  const loadData = async () => {
    // Only org_admin can access roles
    if (user?.role !== 'org_admin') {
      setError('You do not have permission to access roles');
      setRoles([]);
      setPermissions([]);
      return;
    }

    // Prevent duplicate concurrent calls
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const [rolesData, permissionsData] = await Promise.all([
        roleService.getAll(),
        permissionService.getAll() as Promise<Permission[]>
      ]);
      setRoles(rolesData);
      setPermissions(permissionsData);
      setError('');
    } catch (err: any) {
      // Handle 403 Forbidden (permission denied) gracefully
      if (err.response?.status === 403) {
        setError('You do not have permission to access roles');
        setRoles([]);
        setPermissions([]);
      } else {
        setError(err.response?.data?.message || 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || ''
    });
    setShowModal(true);
  };

  const handleManagePermissions = async (role: Role) => {
    setSelectedRole(role);
    setSelectedPermissions(role.permissions?.map((p: Permission) => p.id) || []);
    setShowPermissionModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        setError('Role name is required');
        return;
      }

      if (editingRole) {
        await roleService.update(editingRole.id, formData);
      } else {
        await roleService.create(formData);
      }

      setShowModal(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save role');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this role?')) {
      return;
    }

    try {
      await roleService.delete(id);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete role');
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      await roleService.syncPermissions(selectedRole.id, selectedPermissions);
      setShowPermissionModal(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update permissions');
    }
  };

  const togglePermission = (permissionId: number) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) {
      acc[perm.resource] = [];
    }
    acc[perm.resource].push(perm);
    return acc;
  }, {} as { [key: string]: Permission[] });

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-8">Loading roles...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Roles Management</h1>
            <p className="text-sm text-gray-600 mt-1">Manage user roles and their permissions</p>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center space-x-2 bg-yellow-500 text-black-950 px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors font-bold"
          >
            <Icon component={HiOutlinePlus} className="w-5 h-5" />
            <span>Create Role</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {roles.map((role) => (
                  <tr key={role.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Icon component={HiOutlineUserGroup} className="w-5 h-5 text-yellow-600 mr-2" />
                        <span className="font-medium text-gray-900">{role.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{role.description || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {role.permissions?.length || 0} permissions
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleManagePermissions(role)}
                          className="text-yellow-600 hover:text-yellow-700 flex items-center space-x-1"
                          title="Manage Permissions"
                        >
                          <Icon component={HiOutlineShieldCheck} className="w-4 h-4" />
                          <span>Permissions</span>
                        </button>
                        <button
                          onClick={() => handleEdit(role)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Icon component={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(role.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Icon component={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create/Edit Role Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingRole ? 'Edit Role' : 'Create Role'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon component={HiOutlineX} className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="e.g., sales_manager"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    rows={3}
                    placeholder="Role description..."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 p-6 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 flex items-center space-x-2 font-bold"
                >
                  <Icon component={FiSave} className="w-4 h-4" />
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Permissions Modal */}
        {showPermissionModal && selectedRole && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">
                  Manage Permissions: {selectedRole.name}
                </h2>
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon component={HiOutlineX} className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-6">
                  {Object.entries(groupedPermissions).map(([resource, perms]) => (
                    <div key={resource} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 capitalize">
                        {resource.replace('_', ' ')}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {perms.map((perm) => (
                          <label
                            key={perm.id}
                            className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                              className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                            />
                            <span className="text-sm text-gray-700">{perm.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-between items-center p-6 border-t">
                <span className="text-sm text-gray-600">
                  {selectedPermissions.length} permission(s) selected
                </span>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowPermissionModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePermissions}
                    className="px-4 py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 flex items-center space-x-2 font-bold"
                  >
                    <Icon component={FiSave} className="w-4 h-4" />
                    <span>Save Permissions</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Roles;

